/**
 * TanStack Query bindings for the **deployment-wide** interface read and
 * cert-material upload (ADR-036 §D-5 / §D-7).
 *
 * ``GET /api/admin/peers/interfaces`` answers "every interface in this
 * deployment", which is what the System display's icon stack joins against the
 * status feed. It is chrome data, held open while the app runs.
 *
 * # The interface mutations are not here
 *
 * They nest under their peer now (feature-spec §2.11) and live in
 * :file:`use-peers.ts` beside the peer-scoped read they belong to — a mutation
 * needs a ``peerId`` this flat read deliberately does not carry for a
 * non-privileged session. Keeping them apart is what stops a caller reaching
 * for this module's hook and finding a mutation silently wanting a parent it
 * does not have.
 *
 * Server state lives in the query cache and only there — per coding-standards
 * §2.4 the interface list is never copied into Zustand or a long-lived
 * ``useState``, because a stale copy of an interface's ``state`` is a kill
 * switch showing the wrong position.
 *
 * # Reads are all-roles
 *
 * :func:`usePeerInterfaces` mirrors the BFF's ``_READ_ROLES`` so the surface
 * renders for every authenticated role (REQ-PEER-042), field-limited by the
 * server. Cert upload carries no role check of its own: it renders only for
 * `admin` / `support_engineer` (:data:`PEER_MUTATOR_ROLES`), and the BFF is the
 * enforcement point regardless (ADR-009 defence-in-depth — UI hiding is
 * cosmetic).
 *
 * The System Status snapshot is deliberately **not** invalidated by a peer
 * mutation: an interface's *reported* status arrives on the ADR-035 collector
 * cadence, and force-refetching it would re-render the pre-change reading with a
 * fresh timestamp — an "as of now" marker on stale data is worse than waiting.
 *
 * Cross-references:
 *   - ADR-036 §D-5 (control-plane config), §D-7 (RBAC); feature-spec §2.11.
 *   - REQ-PEER-042 (all-roles read), -043 (privileged mutation), -044 (audited).
 *   - REQ-UI-035 (BFF-only calls); REQ-AUTH-022 (BFF-side RBAC).
 */
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import { useSession } from "@/auth/session-context";
import type { Role } from "@/auth/session-types";
import { parsePeerInterfaceList, type PeerInterfaceView } from "@/peers/peer-model";

/** Roles the BFF lets **read** the peer surface, field-limited (REQ-PEER-042). */
export const PEER_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "support_engineer",
  "admin",
];

/**
 * Roles the BFF lets **mutate** peer configuration (REQ-PEER-043) — a
 * deliberate expansion of `support_engineer`'s write surface beyond
 * disable-only (ADR-036 §D-7; reconcile REQ-AUTH-027).
 *
 * Flow L §1 requires the mutation controls be **absent** for other roles, not
 * disabled: a disabled control tells an operator the system has a capability
 * they may not use, which on a peer-manager surface is an invitation to go
 * looking for a way to use it.
 */
export const PEER_MUTATOR_ROLES: readonly Role[] = ["admin", "support_engineer"];

/** TanStack Query key for the interface list; every mutation invalidates it. */
export const PEER_INTERFACES_KEY: readonly unknown[] = ["peer-manager", "interfaces"];

const BASE = "/api/admin/peers";

/**
 * Fetch every configured Peer Interface.
 *
 * The role gate mirrors the BFF's read gate so a session carrying no known
 * Meridian role never issues the request. The response is shape-guarded at the
 * trust boundary (:func:`parsePeerInterfaceList`) before it reaches a
 * component — a drifted payload fails loudly rather than rendering a
 * half-populated egress path.
 *
 * @param enabled Whether to issue the request at all, ANDed with the role gate.
 *   Defaults to ``true``, which is what every roster caller wants. It exists for
 *   the one caller that needs this list only as a *fallback* — the peer dialog's
 *   handover resolution, which since ADR-039 Phase I matches a peer name first
 *   and reaches this list only when that misses. Before Phase I the System
 *   Status panel held this query open for its icon stack, so the fetch was free
 *   everywhere; the stack now enumerates peers, so the cost is real and belongs
 *   to whoever actually needs it.
 */
export function usePeerInterfaces(enabled = true): UseQueryResult<PeerInterfaceView[], ApiError> {
  const session = useSession();
  return useQuery<PeerInterfaceView[], ApiError>({
    queryKey: PEER_INTERFACES_KEY,
    enabled: enabled && session.hasAnyRole(PEER_READER_ROLES),
    queryFn: async () => parsePeerInterfaceList(await apiFetch<unknown>(`${BASE}/interfaces`)),
  });
}

/** What the operator is uploading — a trust anchor, or the leaf Meridian presents. */
export type CertMaterialKind = "consumer_ca_bundle" | "meridian_cert";

/** One certificate the BFF parsed out of the upload, echoed for out-of-band verification. */
export interface InspectedCertificate {
  subject: string;
  issuer: string;
  not_before: string;
  not_after: string;
  fingerprint_sha256: string;
  is_ca: boolean;
}

/** The cert-material upload response: the server-chosen path plus what it parsed. */
export interface CertMaterialUploadResult {
  kind: string;
  /** The server-generated path an operator pastes into a cert reference field. */
  stored_path: string;
  content_sha256: string;
  certificates: InspectedCertificate[];
}

/** Variables for {@link useUploadCertMaterial}. */
export interface UploadCertMaterialVars {
  kind: CertMaterialKind;
  file: File;
}

/**
 * Upload **public** cert material (``POST …/cert-material``).
 *
 * Accepts a Consumer CA bundle or the Meridian leaf certificate only. **Private
 * keys never transit the application** (ADR-036 OQ-2; ADR-015 key custody is a
 * documented control whose change would trigger re-accreditation) — the UI
 * offers no key upload, and the Meridian keypair stays minted at install and
 * referenced by path.
 *
 * The response echoes subject / issuer / validity / SHA-256 fingerprint so the
 * operator can verify out of band against what the Consumer's operator told
 * them, rather than trusting the transfer. Uploading a CA bundle decides who
 * Meridian is willing to send mission data to, so it is `admin` /
 * `support_engineer` only and audited as a ``ConfigurationChangeEvent``.
 */
export function useUploadCertMaterial(): UseMutationResult<
  CertMaterialUploadResult,
  ApiError,
  UploadCertMaterialVars
> {
  return useMutation<CertMaterialUploadResult, ApiError, UploadCertMaterialVars>({
    mutationFn: async ({ kind, file }) => {
      const form = new FormData();
      form.append("kind", kind);
      form.append("file", file);
      // No Content-Type header: the browser must set the multipart boundary.
      // `apiFetch` leaves a non-string body alone and still injects CSRF.
      return apiFetch<CertMaterialUploadResult>(`${BASE}/cert-material`, {
        method: "POST",
        body: form,
      });
    },
  });
}
